import { XIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { ExpressCourierInfoDialog } from "~/components/express-courier-info-dialog.component";
import { TransformedImage } from "~/components/transformed-image.component";

const DELIVERY_ILLUSTRATION_SRC = "/images/delivery.png";

interface ExpressAvailabilityHeaderCardProps {
	onDismiss: () => void;
}

/**
 * Headline is split into an accented lead ("NEU:"/"NEW:") and the remaining
 * sentence so the two can carry different text styling without a <Trans>.
 * The badge and dismiss control sit in a reserved top strip (Figma:
 * `461 | Uber delivery`, node 2001-37653) rather than inline with the
 * illustration row, so pt on the card - not padding on the row - makes room
 * for them.
 */
export function ExpressAvailabilityHeaderCard({
	onDismiss,
}: Readonly<ExpressAvailabilityHeaderCardProps>) {
	const { t } = useTranslation("common");

	return (
		<div
			data-testid="express-availability-header-card"
			// Figma dark shell is brand-green-600 (#15322c) on small screens only
			// -- darker than the closest existing token (ansay-900, #1c433b),
			// which isn't in the palette under that name yet. Desktop keeps
			// ansay-900 per the design review.
			className="relative flex w-full items-start gap-3 overflow-hidden rounded-xl bg-[#15322c] px-4 pb-4 pt-[44px] md:bg-ansay-900"
		>
			<span className="absolute left-0 top-0 rounded-br-xl bg-ansay-400 px-3 py-2 text-caption-emphasis uppercase tracking-[0.12px] text-ansay-300">
				{t("header.expressCard.badge")}
			</span>
			<button
				type="button"
				onClick={onDismiss}
				aria-label={t("header.expressCard.dismissAriaLabel")}
				data-testid="express-availability-header-card-dismiss"
				className="absolute right-1 top-1 flex size-10 cursor-pointer items-center justify-center text-white"
			>
				<XIcon size={24} aria-hidden="true" />
			</button>
			<TransformedImage
				src={DELIVERY_ILLUSTRATION_SRC}
				alt=""
				width={48}
				height={48}
				loading="lazy"
				className="size-12 shrink-0 object-contain"
			/>
			<div className="flex flex-1 flex-col gap-4">
				<div className="flex flex-col gap-1">
					{/* Figma spec is text-base-bold (font-bold/650) -- text-body-emphasis
					 * is font-black/750, a step heavier than the design. */}
					<p className="text-body-bold text-white">
						<span className="text-ansay-300">
							{t("header.expressCard.headlineHighlight")}
						</span>{" "}
						{t("header.expressCard.headline")}
					</p>
					<p className="text-body-sm tracking-[-0.28px] text-white">
						{t("header.expressCard.body")}
					</p>
				</div>
				<ExpressCourierInfoDialog
					trigger={
						<button
							type="button"
							className="w-fit cursor-pointer text-left text-body-sm-emphasis text-ansay-300"
						>
							{t("header.expressCard.learnMore")}
						</button>
					}
				/>
			</div>
		</div>
	);
}

export function ExpressAvailabilityHeaderCardSkeleton() {
	return (
		<div
			data-testid="express-availability-header-card-skeleton"
			className="h-[168px] w-full shrink-0 animate-pulse rounded-xl bg-ansay-900/40"
		/>
	);
}
